import logger from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import GearsSpinner from './GearsSpinner';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ResultRowsEditor, { EMPTY_ROW, isCompleteRow } from './ResultRowsEditor';

/**
 * LabResultsWorkflow
 * ------------------
 * Per-order lab results template + AI validation, shared by Labs.js (ExamDetail)
 * and Results.js (regular lab list). Operates on a single lab_orders row:
 *   - type results -> generate a branded PDF (with staff-selected reference ranges)
 *   - view the generated report
 *   - run AI validation (name/DOB + ordered test), with a manual-override fallback
 *
 * No fax step (template + AI only).
 *
 * Props:
 *   orderId               lab_orders.id
 *   cptCode               lab_orders.cpt_code (int or string)
 *   examName              lab_orders.exam_ordered (used as a name fallback when the
 *                         CPT is missing or not in the reference-range catalog)
 *   patient               { first_name, last_name }
 *   initialResultsPdfPath results_pdf_path already on the order (or null)
 *   initialValidatedAt    validated_at already on the order (or null)
 *   onUpdated             optional () => void, called after generate/validate so a
 *                         parent list can refresh
 */
const LabResultsWorkflow = ({
  orderId,
  cptCode,
  examName,
  patient = {},
  initialResultsPdfPath = null,
  initialValidatedAt = null,
  onUpdated,
}) => {
  const BASE_URL = process.env.REACT_APP_API_URL;

  const [resultsPdfPath, setResultsPdfPath] = useState(initialResultsPdfPath);
  const [validatedAt, setValidatedAt] = useState(initialValidatedAt);

  // Structured analyte rows — posted as result_rows so the generated PDF matches the
  // analyzer report and the values run through the same abnormal evaluation.
  const [resultRows, setResultRows] = useState([{ ...EMPTY_ROW }]);
  const [generating, setGenerating] = useState(false);
  const [generateMsg, setGenerateMsg] = useState('');

  const [ranges, setRanges] = useState({});            // keyed by cpt code
  const [checkedRanges, setCheckedRanges] = useState({});

  const [reportModal, setReportModal] = useState(false);
  const [reportPdfUrl, setReportPdfUrl] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [validationModal, setValidationModal] = useState(false);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // Keep local state in sync if the parent swaps to a different order.
  useEffect(() => {
    setResultsPdfPath(initialResultsPdfPath);
    setValidatedAt(initialValidatedAt);
    setResultRows([{ ...EMPTY_ROW }]);
    setGenerateMsg('');
  }, [orderId]); // eslint-disable-line

  // Fetch the reference ranges for this order — by CPT code, falling back to the
  // test name when the CPT is missing or not in the catalog.
  useEffect(() => {
    const hasCode = cptCode != null && cptCode !== '';
    if (!hasCode && !examName) return;
    const params = new URLSearchParams();
    if (hasCode) params.set('codes', String(cptCode));
    if (examName) params.set('name', examName);
    fetch(`${BASE_URL}/labs/reference-ranges?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        setRanges(data || {});
        const checked = {};
        for (const [c, ref] of Object.entries(data || {})) {
          if (ref.rows) ref.rows.forEach((_, idx) => { checked[`${c}_${idx}`] = true; });
        }
        setCheckedRanges(checked);
      })
      .catch(err => logger.error('Error fetching reference ranges:', err));
  }, [cptCode, examName]); // eslint-disable-line

  const toggleCheck = (code, idx) => {
    const key = `${code}_${idx}`;
    setCheckedRanges(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const buildSelectedRanges = () => {
    const result = [];
    for (const [code, ref] of Object.entries(ranges)) {
      if (!ref || !ref.rows) continue;
      const checkedRows = ref.rows.filter((_, idx) => checkedRanges[`${code}_${idx}`]);
      if (checkedRows.length === 0) continue;
      result.push({ cpt_code: code, name: ref.name, unit: ref.unit || null, rows: checkedRows, note: ref.note || null });
    }
    return result;
  };

  const filledRows = resultRows.filter(isCompleteRow);

  const generateReport = async () => {
    if (filledRows.length === 0) return;
    setGenerating(true);
    setGenerateMsg('');
    try {
      const response = await fetch(`${BASE_URL}/labs/order/${orderId}/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ result_rows: filledRows, selected_ranges: buildSelectedRanges() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.msg || 'Failed to generate report');
      setGenerateMsg(
        data.abnormalStatus === 'abnormal'
          ? 'Report generated — ABNORMAL result flagged.'
          : 'Report generated successfully.'
      );
      setResultRows([{ ...EMPTY_ROW }]);
      setResultsPdfPath(data.results_pdf_path);
      setValidatedAt(null); // a fresh report clears prior validation
      if (onUpdated) onUpdated();
      toast.success('Report generated successfully');
    } catch (error) {
      logger.error('Error generating lab results:', error);
      setGenerateMsg(`Error: ${error.message}`);
      toast.error(`Failed to generate report: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const openReportModal = async () => {
    if (!resultsPdfPath) return;
    setReportLoading(true);
    setReportPdfUrl(null);
    setReportModal(true);
    try {
      const response = await fetch(`${BASE_URL}/upload/generate-signed-url?pdfName=${encodeURIComponent(resultsPdfPath)}`);
      const data = await response.json();
      if (data.signedUrl) setReportPdfUrl(data.signedUrl);
    } catch (error) {
      logger.error('Error generating signed URL:', error);
    } finally {
      setReportLoading(false);
    }
  };

  const runValidation = async (manual = false) => {
    setValidationLoading(true);
    setValidationError(null);
    setValidationResult(null);
    try {
      const response = await fetch(`${BASE_URL}/labs/order/${orderId}/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manual }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.msg || 'Validation failed');
      setValidationResult(data.validationResult);
      if (data.validated_at) {
        setValidatedAt(data.validated_at);
        if (onUpdated) onUpdated();
        toast.success(manual ? 'Marked as manually validated' : 'Validation passed');
      } else {
        toast.warn('Validation did not pass — review the checks');
      }
    } catch (err) {
      logger.error('Validation error:', err);
      setValidationError(err.message);
      toast.error('Unable to complete validation');
    } finally {
      setValidationLoading(false);
    }
  };

  const patientName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
  const rangeEntries = Object.entries(ranges);

  return (
    <div>
      <div style={{ padding: '16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px' }}>
        <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#166534' }}>Lab Results</h4>

        {resultsPdfPath ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', color: '#166534' }}>Report on file.</span>
            <button className="ris-bigger-blue-button" onClick={openReportModal}>View Report</button>
            {validatedAt && (
              <span className="ris-validation-badge ris-validation-badge-pass">Validated</span>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Structured results entry — one analyte per row */}
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', color: '#166534', marginBottom: '6px' }}>
                Results — one analyte per row
              </label>
              <ResultRowsEditor
                rows={resultRows}
                onChange={(next) => { setResultRows(next); setGenerateMsg(''); }}
                disabled={generating}
              />
            </div>

            {/* Reference range checkboxes */}
            {rangeEntries.length > 0 && (
              <div>
                <p style={{ fontWeight: 600, fontSize: '13px', color: '#166534', marginBottom: '8px' }}>
                  Reference Ranges — check applicable ranges to include in report:
                </p>
                {rangeEntries.map(([code, ref]) => {
                  if (!ref || !ref.rows) return null;
                  return (
                    <div key={code} style={{ marginBottom: '10px', padding: '10px 12px', background: '#fff', border: '1px solid #86efac', borderRadius: '6px' }}>
                      <p style={{ fontWeight: 600, fontSize: '12px', color: '#0e8a8f', marginBottom: '6px' }}>
                        {ref.name}{ref.unit ? ` (${ref.unit})` : ''}
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {ref.rows.map(([label, range], idx) => (
                          <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer', color: '#374151' }}>
                            <input
                              type="checkbox"
                              checked={!!checkedRanges[`${code}_${idx}`]}
                              onChange={() => toggleCheck(code, idx)}
                              disabled={generating}
                              style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                            />
                            <span style={{ flex: 1 }}>{label}</span>
                            <span style={{ color: '#6b7280', minWidth: '120px', textAlign: 'right' }}>{range}</span>
                          </label>
                        ))}
                      </div>
                      {ref.note && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>* {ref.note}</p>}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                className="ris-bigger-blue-button"
                onClick={generateReport}
                disabled={filledRows.length === 0 || generating}
              >
                {generating ? 'Generating Report…' : 'Generate Report'}
              </button>
              {generateMsg && (
                <span style={{ fontSize: '13px', color: generateMsg.startsWith('Error') ? '#dc2626' : '#166534' }}>
                  {generateMsg}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Report viewer modal ─────────────────────────────────────────── */}
      {reportModal && (
        <div className="pdfViewer-modal-overlay open">
          <div className="pdfViewer-modal-content">
            <div className="pdfViewer-modal-header">
              <h3>Lab Results Report — {patientName}</h3>
            </div>

            <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              {reportLoading ? (
                <p style={{ padding: '20px', textAlign: 'center' }}>Loading report…</p>
              ) : reportPdfUrl ? (
                <iframe
                  src={reportPdfUrl}
                  title="Lab Results Report"
                  width="100%"
                  height="100%"
                  className="pdfViewer-modal-element"
                  style={{ border: 'none', display: 'block' }}
                />
              ) : (
                <p style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>
                  Failed to load report. Please try again.
                </p>
              )}
            </div>

            <div className="pdfViewer-modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {/* Replace Results */}
              <button
                className="ris-modal-btn-replace"
                onClick={() => {
                  setReportModal(false);
                  setReportPdfUrl(null);
                  setResultRows([{ ...EMPTY_ROW }]);
                  setGenerateMsg('');
                  setResultsPdfPath(null);
                  setValidatedAt(null);
                  if (onUpdated) onUpdated();
                }}
              >
                Replace Results
              </button>

              {/* Validate — opens validation modal and runs AI check */}
              <button
                className="ris-modal-btn-validate"
                onClick={() => { setValidationModal(true); runValidation(false); }}
              >
                Validate
              </button>

              <button
                className="pdfViewer-close-button"
                onClick={() => { setReportModal(false); setReportPdfUrl(null); }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Validation modal ────────────────────────────────────────────── */}
      {validationModal && (
        <div className="pdfViewer-modal-overlay open" style={{ zIndex: 1200 }}>
          <div
            className="pdfViewer-modal-content"
            style={{ width: '520px', height: 'auto', maxWidth: '92vw', padding: '0' }}
          >
            <div className="pdfViewer-modal-header">
              <h3>AI Validation</h3>
              <p className="pdfViewer-modal-subtitle">
                Lab Order #{orderId} — {patientName}
              </p>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {validationLoading && (
                <div className="ris-validation-loading">
                  <GearsSpinner alt="Validating…" />
                  <p>Analyzing document…</p>
                </div>
              )}

              {!validationLoading && validationError && (
                <>
                  <div className="ris-validation-error">
                    {validationError}
                  </div>
                  <button
                    className="ris-manual-validate-btn"
                    onClick={() => runValidation(true)}
                  >
                    Unable to reach AI — Mark as Manually Validated
                  </button>
                </>
              )}

              {!validationLoading && validationResult && (
                <div className="ris-validation-cards">
                  {/* Name Check */}
                  <div className="ris-validation-card">
                    <div className="ris-validation-card-header">
                      <span className="ris-validation-card-title">Name Check</span>
                      <span className={`ris-validation-badge ${validationResult.name_check?.pass ? 'ris-validation-badge-pass' : 'ris-validation-badge-fail'}`}>
                        {validationResult.name_check?.pass ? 'Pass' : 'Fail'}
                      </span>
                    </div>
                    <div className="ris-validation-card-body">
                      {validationResult.name_check?.found_name && (
                        <div className="ris-validation-row">
                          <span className="ris-validation-label">Name found:</span>
                          <span className="ris-validation-value">{validationResult.name_check.found_name}</span>
                        </div>
                      )}
                      {validationResult.name_check?.found_dob && (
                        <div className="ris-validation-row">
                          <span className="ris-validation-label">DOB found:</span>
                          <span className="ris-validation-value">{validationResult.name_check.found_dob}</span>
                        </div>
                      )}
                      {validationResult.name_check?.found_age && (
                        <div className="ris-validation-row">
                          <span className="ris-validation-label">Age found:</span>
                          <span className="ris-validation-value">{validationResult.name_check.found_age}</span>
                        </div>
                      )}
                      {validationResult.name_check?.details && (
                        <p className="ris-validation-details">{validationResult.name_check.details}</p>
                      )}
                    </div>
                  </div>

                  {/* Order Check */}
                  <div className="ris-validation-card">
                    <div className="ris-validation-card-header">
                      <span className="ris-validation-card-title">Order Check</span>
                      <span className={`ris-validation-badge ${validationResult.order_check?.pass ? 'ris-validation-badge-pass' : 'ris-validation-badge-fail'}`}>
                        {validationResult.order_check?.pass ? 'Pass' : 'Fail'}
                      </span>
                    </div>
                    <div className="ris-validation-card-body">
                      {validationResult.order_check?.found_tests?.length > 0 && (
                        <div className="ris-validation-row">
                          <span className="ris-validation-label">Tests found:</span>
                          <span className="ris-validation-value">{validationResult.order_check.found_tests.join(', ')}</span>
                        </div>
                      )}
                      {validationResult.order_check?.details && (
                        <p className="ris-validation-details">{validationResult.order_check.details}</p>
                      )}
                    </div>
                  </div>

                  {/* Manual badge if this was a staff override */}
                  {validationResult.manual && (
                    <p style={{ textAlign: 'center', margin: 0 }}>
                      <span className="ris-validation-badge ris-validation-badge-manual">Manually Validated</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="pdfViewer-modal-footer">
              <button
                className="pdfViewer-close-button"
                onClick={() => { setValidationModal(false); setValidationResult(null); setValidationError(null); }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabResultsWorkflow;
